import * as React from 'react'

import { classList } from 'react-classlist-helper'

import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import { ChevronDownIcon } from '~/client/src/shared/components/icons/ChevronDownIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  useWorkflowFieldRestriction,
  WorkflowFieldRestrictionItem,
} from './WorkflowFieldRestrictionContext'

interface IProps {
  renderItem: (item: WorkflowFieldRestrictionItem) => JSX.Element
  rowsCount?: number
  isOpen?: boolean
}

const PADDING_WIDTH = 16
const MORE_BUTTON_WIDTH = 94
const DEFAULT_MIN_ROWS = 3

const WorkflowFieldRestrictionChipArea: React.FC<IProps> = ({
  renderItem,
  rowsCount = DEFAULT_MIN_ROWS,
  isOpen = false,
}) => {
  const { state, meta } = useWorkflowFieldRestriction()
  const [isExpanded, setIsExpanded] = React.useState(false)

  const handleToggleExpanded = React.useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
    setIsExpanded(prev => !prev)
  }, [])

  if (state.isAllSelected || state.items.length === 0) {
    return (
      <div
        className={classList({
          'tw:rounded tw:cursor-pointer tw:px-2 tw:py-[6px] tw:w-full tw:min-w-[300px] tw:h-10 tw:flex tw:items-center tw:justify-between': true,
          'tw:border tw:border-solid tw:border-border': !isOpen,
          'tw:border tw:border-solid tw:border-primary': isOpen,
        })}
      >
        <span className="tw:text-body-lg tw:text-foreground tw:select-none">
          {meta.emptyLabel}
        </span>
        <ChevronDownIcon
          size="xs"
          className={classList({
            'tw:flex-none tw:text-muted-foreground': true,
            'tw:rotate-180': isOpen,
          })}
        />
      </div>
    )
  }

  const chipElements = state.items.map(item => renderItem(item))

  return (
    <DynamicOverflowList
      shouldIgnoreScrollBarWidth
      shouldRecalculateRowsCount
      items={chipElements}
      rowsCount={isExpanded ? chipElements.length : rowsCount}
      paddingWidth={PADDING_WIDTH}
      moreButtonWidth={MORE_BUTTON_WIDTH}
      elementsClassName="tw:align-middle tw:overflow-hidden"
    >
      {({ visibleElements, overflowElements, containerRefSetter }) => (
        <div
          ref={containerRefSetter}
          className={classList({
            'tw:rounded tw:cursor-pointer tw:min-w-[300px] tw:pl-2 tw:pr-7 tw:py-[6px] tw:overflow-y-auto tw:overflow-x-hidden tw:relative': true,
            'tw:border tw:border-solid tw:border-border': !isOpen,
            'tw:border tw:border-solid tw:border-primary': isOpen,
            'tw:max-h-[70vh]': isExpanded,
          })}
        >
          {visibleElements}
          {(overflowElements.length > 0 || isExpanded) && (
            <MoreButton
              isExpanded={isExpanded}
              overflowCount={overflowElements.length}
              onClick={handleToggleExpanded}
            />
          )}
          <ChevronDownIcon
            size="xs"
            className={classList({
              'tw:absolute tw:top-2 tw:right-2 tw:flex-none tw:text-muted-foreground': true,
              'tw:rotate-180': isOpen,
            })}
          />
        </div>
      )}
    </DynamicOverflowList>
  )
}

interface IMoreButtonProps {
  isExpanded: boolean
  overflowCount: number
  onClick: (e: React.SyntheticEvent) => void
}

const MoreButton: React.FC<IMoreButtonProps> = ({
  isExpanded,
  overflowCount,
  onClick,
}) => {
  return (
    <div
      className="tw:inline-flex tw:items-center tw:align-middle tw:h-5 tw:rounded-[2px] tw:bg-primary-foreground tw:border tw:border-solid tw:border-border tw:my-0.5 tw:mr-1 tw:cursor-pointer"
      onClick={onClick}
      title={
        isExpanded
          ? Localization.translator.showLess
          : `+${overflowCount} ${Localization.translator.more}`
      }
    >
      <span className="tw:text-body-md tw:font-medium tw:text-foreground tw:flex-none tw:px-2">
        {isExpanded
          ? Localization.translator.showLess
          : `+${overflowCount} ${Localization.translator.more}`}
      </span>
    </div>
  )
}

export default WorkflowFieldRestrictionChipArea
